package org.teddapp.project.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

/** Ton d'un tag. Un signal (succès, attention, danger) porte toujours un mot ou une icône. */
enum class TeddTagTone {
    /** Métadonnée sans importance particulière. */
    Neutral,
    /** Ce qui est en cours : remplissage citron vert. */
    Lime,
    /** Teinte de marque douce : sélection, appartenance. */
    Brand,
    Success, Warning, Danger,
    /** Ce qui est public : latérite. */
    Public,
    Outline,
}

/**
 * Tag du design system : pastille de 24 dp, toujours avec un libellé.
 * [dot] ajoute un point de la couleur du texte ; [dotColor] le teinte (repère de module).
 */
@Composable
fun TeddTag(
    text: String,
    modifier: Modifier = Modifier,
    tone: TeddTagTone = TeddTagTone.Neutral,
    icon: String? = null,
    dot: Boolean = false,
    dotColor: Color? = null,
) {
    val colors = LocalTeddColors.current
    val bg: Color
    val fg: Color
    when (tone) {
        TeddTagTone.Neutral -> { bg = colors.surfaceSunken; fg = colors.inkMuted }
        TeddTagTone.Lime -> { bg = colors.lime; fg = colors.onLime }
        TeddTagTone.Brand -> { bg = colors.limeSoft; fg = colors.ink }
        TeddTagTone.Success -> { bg = colors.successSoft; fg = colors.success }
        TeddTagTone.Warning -> { bg = colors.warningSoft; fg = colors.warning }
        TeddTagTone.Danger -> { bg = colors.dangerSoft; fg = colors.danger }
        TeddTagTone.Public -> { bg = colors.lateriteSoft; fg = colors.laterite }
        TeddTagTone.Outline -> { bg = Color.Transparent; fg = colors.inkMuted }
    }
    val shape = RoundedCornerShape(TeddTokens.RFull)
    Row(
        modifier = modifier
            .clip(shape)
            .background(bg, shape)
            .then(if (tone == TeddTagTone.Outline) Modifier.border(1.dp, colors.line, shape) else Modifier)
            .height(24.dp)
            .padding(horizontal = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        if (dot || dotColor != null) {
            Box(Modifier.size(7.dp).background(dotColor ?: fg, CircleShape))
        }
        if (icon != null) TeddIcon(name = icon, size = 13.dp, color = fg)
        Text(text = text, color = fg, style = TeddType.Caption.copy(fontWeight = FontWeight(520)), maxLines = 1)
    }
}

/** État de visibilité d'un contenu : rien ne sort du privé sans être marqué. */
enum class TeddVisibility { Private, Draft, Public }

/**
 * Badge de visibilité. Privé : neutre, cadenas. Brouillon : contour latérite en tirets.
 * Public : latérite, globe. La latérite n'est employée nulle part ailleurs dans l'app.
 */
@Composable
fun TeddVisibilityBadge(state: TeddVisibility, modifier: Modifier = Modifier, label: String? = null) {
    val colors = LocalTeddColors.current
    val shape = RoundedCornerShape(TeddTokens.RFull)
    val (bg, fg, text) = when (state) {
        TeddVisibility.Private -> Triple(colors.surfaceSunken, colors.inkMuted, "Privé")
        TeddVisibility.Draft -> Triple(Color.Transparent, colors.laterite, "Brouillon public")
        TeddVisibility.Public -> Triple(colors.lateriteSoft, colors.laterite, "Public")
    }
    Row(
        modifier = modifier
            .clip(shape)
            .background(bg, shape)
            .then(
                if (state == TeddVisibility.Draft) Modifier.drawBehind {
                    drawRoundRect(
                        color = fg,
                        cornerRadius = CornerRadius(size.height / 2),
                        style = Stroke(width = 1.dp.toPx(), pathEffect = PathEffect.dashPathEffect(floatArrayOf(4f, 3f))),
                    )
                } else Modifier,
            )
            .height(24.dp)
            .padding(start = 8.dp, end = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        TeddIcon(name = if (state == TeddVisibility.Private) "lock" else "globe", size = 13.dp, color = fg)
        Text(text = label ?: text, color = fg, style = TeddType.Caption.copy(fontWeight = FontWeight(540)), maxLines = 1)
    }
}

// ── Ancienne étiquette ─────────────────────────────────────────────────
// Gardée le temps de reprendre chaque écran : préférer [TeddTag].

enum class ChipTone { Soft, Solid, Ghost, Neutral }
enum class ChipSize { Sm, Md }

@Composable
fun TeddChip(
    text: String,
    modifier: Modifier = Modifier,
    color: Color? = null,
    tone: ChipTone = ChipTone.Soft,
    size: ChipSize = ChipSize.Md,
    leadingIcon: (@Composable () -> Unit)? = null,
) {
    val colors = LocalTeddColors.current
    val shape = RoundedCornerShape(TeddTokens.RFull)
    val bg: Color
    val fg: Color
    val border: Color
    when {
        // Une couleur passée par l'appelant ne colore plus le fond : elle devient un point.
        tone == ChipTone.Solid -> { bg = colors.lime; fg = colors.onLime; border = Color.Transparent }
        tone == ChipTone.Ghost -> { bg = Color.Transparent; fg = colors.inkMuted; border = colors.line }
        else -> { bg = colors.surfaceSunken; fg = colors.inkMuted; border = Color.Transparent }
    }
    Row(
        modifier = modifier
            .clip(shape)
            .background(bg, shape)
            .border(1.dp, border, shape)
            .padding(horizontal = if (size == ChipSize.Sm) 8.dp else 10.dp, vertical = if (size == ChipSize.Sm) 2.dp else 4.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(5.dp),
    ) {
        if (color != null && tone == ChipTone.Soft) Box(Modifier.size(6.dp).background(color, CircleShape))
        if (leadingIcon != null) leadingIcon()
        Text(
            text = text,
            color = fg,
            fontSize = if (size == ChipSize.Sm) 11.sp else 12.sp,
            fontWeight = FontWeight(520),
            maxLines = 1,
        )
    }
}
